import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { Status } from '@docma-mm-core/status/status';
import { StatusService } from '@docma-mm-core/status/status.service';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import {ViewPoint} from '@docma-mm-core/status/viewpoint.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsActivityStatusFilterService implements OnDestroy {

  private currentProjectId: number;
  private scStatusesSubject = new BehaviorSubject<SelectItem[]>([]);
  private ctStatusesSubject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();

  public scStatuses$ = this.scStatusesSubject.asObservable();
  public ctStatuses$ = this.ctStatusesSubject.asObservable();

  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly statusService: StatusService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadStatuses(project.MyDocmaUid);
      });
  }

  private async loadStatuses(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);
      const statuses = await db.getAll(MmProjectDbCollections.statuses as never) as Status[];

      const scStatuses = statuses.filter(s => s.ViewPoint === ViewPoint.Contractor);
      const ctStatuses = statuses.filter(s => s.ViewPoint === ViewPoint.Customer);

      const scSelectItems = this.formatStatusesForDropdown(scStatuses || []);
      const ctSelectItems = this.formatStatusesForDropdown(ctStatuses || []);

      this.scStatusesSubject.next(scSelectItems);
      this.ctStatusesSubject.next(ctSelectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsActivityStatusFilterService');
      this.scStatusesSubject.next([]);
      this.ctStatusesSubject.next([]);
    }
  }

  private formatStatusesForDropdown(statuses: Status[]): SelectItem[] {
    const selectItems: SelectItem[] = [];

    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });

    const sortedStatuses = statuses
      .filter(status => status.Id && status.Name1)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));

    sortedStatuses.forEach(status => {
      const name1 = status.Name1 || '';
      const name2 = status.Name2 || '';
      const displayText = name1 && name2 ? `${name1} - ${name2}` : (name1 || name2 || '');

      selectItems.push({
        id: status.Id.toString(),
        text: displayText
      });
    });

    return selectItems;
  }

  public getSCStatusDisplayNameById(statusId: string): string {
    const statuses = this.scStatusesSubject.value;
    const status = statuses.find(s => s.id === statusId);
    return status?.text || statusId;
  }

  public getCTStatusDisplayNameById(statusId: string): string {
    const statuses = this.ctStatusesSubject.value;
    const status = statuses.find(s => s.id === statusId);
    return status?.text || statusId;
  }
}
